import React, { useState, useRef } from "react";
import { Space, Table, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import TherapyForm from "./TherapyForm";
import { getTablePagination } from "../../../utils/tablePagination";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";
import { appConfirmModal } from "../../../helpers/appConfirmModal";

const { Paragraph, Link } = Typography;

const TherapiesTable = ({
  data,
  refresh,
  loading,
  deleteTherapy,
  page,
  setPage,
  total,
}) => {
  const [editData, setEditData] = useState({});
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    Array.isArray(data) ? data.length : 0
  );

  const handleDelete = (id) => {
    appConfirmModal({
      title: "Are you sure?",
      content: "This therapy will be permanently deleted",
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: () => deleteTherapy(id),
    });
  };

  const columns = [
    {
      title: "Therapy Name",
      dataIndex: "name",
      key: "name",
      width: isMobile ? 140 : 220,
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: isMobile ? 220 : 420,
      render: (val) => {
        const text = val?.trim();
        if (!text) return "-";
        return (
          <Paragraph
            style={{ marginBottom: 0, wordBreak: "break-word" }}
            ellipsis={{
              rows: 2,
              expandable: "collapsible",
              symbol: (expanded) => (
                <Link style={{ fontSize: 12, marginLeft: 4 }}>
                  {expanded ? "less" : "more"}
                </Link>
              ),
            }}
          >
            {text}
          </Paragraph>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: isMobile ? 96 : 110,
      fixed: isMobile ? "right" : undefined,
      render: (_, record) => (
        <Space size={8}>
          <ActionIconButton
            aria-label="Edit therapy"
            onClick={() => {
              setEditData(record);
              setOpen(true);
            }}
          >
            <EditIcon />
          </ActionIconButton>
          <ActionIconButton
            aria-label="Delete therapy"
            danger
            onClick={() => handleDelete(record.id)}
          >
            <DeleteOutlined style={{ color: "red" }} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div ref={tableWrapRef} className="ui-table-fill-body">
        <Table
          className="no-vertical-borders"
          columns={columns}
          dataSource={data}
          rowKey="id"
          tableLayout="fixed"
          pagination={getTablePagination({
            current: page,
            pageSize: 50,
            total: total ?? 0,
            onChange: (p) => setPage(p),
          })}
          loading={loading}
          scroll={tableScroll}
        />
      </div>

      {open && (
        <TherapyForm
          open={open}
          setOpen={setOpen}
          editData={editData}
          refresh={refresh}
        />
      )}
    </>
  );
};

export default TherapiesTable;
